import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import EventShareControl from '../components/EventShareControl';
import InstallAppMenuItem from '../components/InstallAppMenuItem';
import { rememberDeepLink } from '../utils/deepLink';
import {
  ArrowLeft, MapPin, Clock, ShieldAlert, ShieldCheck, Eye, Pin, Megaphone, Archive,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_STYLES = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  normal: 'bg-slate-100 text-slate-700',
  low: 'bg-slate-100 text-slate-500',
};

const SafetyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const includeExpired = searchParams.get('includeExpired') === '1';
  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    rememberDeepLink(`/safety/${id}${includeExpired ? '?includeExpired=1' : ''}`);
    const url = `${API}/api/safety-bulletin/${id}${includeExpired ? '?includeExpired=true' : ''}`;
    axios.get(url)
      .then((res) => setBulletin(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, includeExpired]);

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://find-2-day.com'}/api/share/safety/${id}`;

  const fmtDateTime = (d) => new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="safety-detail-loading">
        <div className="animate-pulse text-gray-400">{t('loading', 'Loading…')}</div>
      </div>
    );
  }

  if (notFound || !bulletin) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center" data-testid="safety-detail-notfound">
        <ShieldAlert className="h-12 w-12 text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800">{t('safetyDetail.notFoundTitle', 'Alert not available')}</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-xs">{t('safetyDetail.notFoundBody', 'This safety alert may have expired or been removed.')}</p>
        <button onClick={() => navigate('/safety-bulletin')} className="mt-5 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700" data-testid="safety-detail-browse-btn">
          {t('safetyDetail.browse', 'View safety alerts')}
        </button>
      </div>
    );
  }

  const isOfficial = bulletin.alertSource === 'town_hall' || bulletin.alertSource === 'police';
  const priority = bulletin.priority || 'normal';

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="safety-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm" data-testid="safety-detail-back">
            <ArrowLeft className="h-4 w-4" /> {t('back', 'Back')}
          </button>
          <EventShareControl
            url={shareUrl}
            title={`${bulletin.title} – ${bulletin.town || ''}`.trim()}
            text={bulletin.body || t('shareDefaultText', 'Check out this on Find-2-Day')}
            testId={`safety-detail-${bulletin.id}`}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4">
        {/* Jul 2026 — Archived/expired banner for pinned-notification opens. */}
        {bulletin.isExpired && (
          <div
            className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
            data-testid="safety-detail-expired-banner"
            role="status"
          >
            <Archive className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
            <p>
              {t(
                'safetyDetail.expiredBanner',
                'This alert has expired and is shown for reference only.',
              )}
            </p>
          </div>
        )}

        {/* Badges */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {(priority === 'critical' || priority === 'high') && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${PRIORITY_STYLES[priority]}`} data-testid="safety-detail-priority">
              <ShieldAlert className="h-3 w-3" /> {t(`safety.priority.${priority}`, priority === 'critical' ? 'Critical' : 'High')}
            </span>
          )}
          {bulletin.category && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium" data-testid="safety-detail-category">
              {bulletin.category}
            </span>
          )}
          {bulletin.isBroadcast && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium" data-testid="safety-detail-broadcast">
              <Megaphone className="h-3 w-3" /> {t('safety.broadcast', 'All towns')}
            </span>
          )}
          {bulletin.isPinned && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium" data-testid="safety-detail-pinned">
              <Pin className="h-3 w-3" /> {t('safety.pinned', 'Pinned')}
            </span>
          )}
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mt-2" data-testid="safety-detail-title">{bulletin.title}</h1>

        {/* Meta */}
        <div className="mt-3 bg-white rounded-xl border border-gray-100 shadow-sm divide-y divide-gray-50">
          <div className="flex items-center gap-3 p-4 text-sm" data-testid="safety-detail-author">
            {isOfficial ? <ShieldCheck className="h-5 w-5 text-emerald-500 shrink-0" /> : <ShieldAlert className="h-5 w-5 text-purple-500 shrink-0" />}
            <span className="text-gray-800 font-medium">{bulletin.authorDisplay}</span>
            {isOfficial && (
              <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                {t('safety.verified', 'Verified')}
              </span>
            )}
          </div>
          {bulletin.town && !bulletin.isBroadcast && (
            <div className="flex items-center gap-3 p-4 text-sm" data-testid="safety-detail-town">
              <MapPin className="h-5 w-5 text-purple-500 shrink-0" />
              <span className="text-gray-800">{bulletin.town}</span>
            </div>
          )}
          {bulletin.publishedAt && (
            <div className="flex items-center gap-3 p-4 text-sm" data-testid="safety-detail-published">
              <Clock className="h-5 w-5 text-purple-500 shrink-0" />
              <span className="text-gray-800">{fmtDateTime(bulletin.publishedAt)}</span>
            </div>
          )}
        </div>

        {/* Body */}
        {bulletin.body && (
          <div className="mt-5" data-testid="safety-detail-body">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{bulletin.body}</p>
          </div>
        )}

        {/* Seen-by social proof */}
        {bulletin.seenByCount > 0 && (
          <div className="mt-4 flex items-center gap-1.5 text-xs text-gray-400" data-testid="safety-detail-seen">
            <Eye className="h-3.5 w-3.5" /> {t('safety.seenBy', { count: bulletin.seenByCount, defaultValue: `Seen by ${bulletin.seenByCount}` })}
          </div>
        )}

        {/* Install app prompt (hidden if already installed) */}
        <div className="mt-6">
          <InstallAppMenuItem surface="safety_detail" variant="card" testId="safety-detail-install" />
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default SafetyDetail;
